<template>
  <div style="overflow-x: auto;">
    <div class="d-flex align-center mb-2">
      <v-spacer></v-spacer>
      <v-btn small color="primary" @click="openAddDialog">
        <v-icon small left>mdi-plus</v-icon>
        {{ $t('components.TimeRestrictionsTable.addNewRestriction') }}
      </v-btn>
    </div>
    <v-simple-table dense>
      <template v-slot:default>
        <thead>
          <tr>
            <th class="text-left" style="position: sticky; left: 0; background: white; z-index: 1; min-width: 200px;">
              {{ $t('components.TimeRestrictionsTable.mediaChannel') }}
            </th>
            <th
              v-for="(header, index) in formattedPeriodHeaders"
              :key="index"
              class="text-center"
              style="min-width: 80px;"
            >
              {{ header }}
            </th>
          </tr>
        </thead>
        <tbody>
          <template v-if="hasAllChannelRestrictions">
            <tr
              v-for="(lane, li) in channelLaneMap[ALL_CHANNELS_ID]"
              :key="'all-lane-' + li"
              :class="{ 'lane-last': li === channelLaneMap[ALL_CHANNELS_ID].length - 1 }"
            >
              <td
                v-if="li === 0"
                :rowspan="channelLaneMap[ALL_CHANNELS_ID].length"
                class="channel-name-cell"
                style="position: sticky; left: 0; background: white; z-index: 1; font-weight: 500; vertical-align: middle;"
              >
                {{ $t('components.TimeRestrictionsTable.allChannels') }}
              </td>
              <template v-for="(seg, si) in lane">
                <td
                  v-if="seg.type === 'empty'"
                  :key="'all-' + li + '-' + si"
                  :colspan="seg.span"
                  class="text-center grey--text text--lighten-1 pa-0"
                >
                  <span v-if="seg.span === 1">&mdash;</span>
                </td>
                <td
                  v-else
                  :key="'all-' + li + '-' + si"
                  :colspan="seg.span"
                  class="pa-1"
                >
                  <div class="restriction-bar rounded px-2 caption white--text d-flex align-center" :style="{ background: seg.restriction.timespan === 'full_period' ? '#B39DDB' : '#7E57C2', whiteSpace: 'nowrap', overflow: 'hidden', lineHeight: '24px' }">
                    <v-tooltip v-if="seg.restriction.timespan === 'full_period'" bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <span class="fp-tag" v-bind="attrs" v-on="on">fp</span>
                      </template>
                      <span>{{ $t('components.TimeRestrictionsTable.fullPeriod') }}</span>
                    </v-tooltip>
                    <span class="text-truncate flex-grow-1 text-center">{{ formatRestriction(seg.restriction) }}</span>
                    <v-menu offset-y left>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon x-small color="white" class="ml-1" v-bind="attrs" v-on="on" style="flex-shrink: 0;">mdi-chevron-down</v-icon>
                      </template>
                      <v-list dense>
                        <v-list-item @click="editRestriction(seg.restriction)">
                          <v-list-item-icon class="mr-2"><v-icon small>mdi-pencil</v-icon></v-list-item-icon>
                          <v-list-item-title>{{ $t('components.TimeRestrictionsTable.edit') }}</v-list-item-title>
                        </v-list-item>
                        <v-list-item @click="deleteRestriction(seg.restriction)">
                          <v-list-item-icon class="mr-2"><v-icon small color="red">mdi-delete</v-icon></v-list-item-icon>
                          <v-list-item-title class="red--text">{{ $t('components.TimeRestrictionsTable.delete') }}</v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </div>
                </td>
              </template>
            </tr>
          </template>
          <template v-for="channel in channels">
            <tr
              v-for="(lane, li) in channelLaneMap[channel[0]]"
              :key="channel[0] + '-lane-' + li"
              :class="{ 'lane-last': li === channelLaneMap[channel[0]].length - 1 }"
            >
              <td
                v-if="li === 0"
                :rowspan="channelLaneMap[channel[0]].length"
                class="channel-name-cell"
                style="position: sticky; left: 0; background: white; z-index: 1; vertical-align: middle;"
              >
                {{ channelName(channel[0]) }}
              </td>
              <template v-for="(seg, si) in lane">
                <td
                  v-if="seg.type === 'empty'"
                  :key="'s-' + li + '-' + si"
                  :colspan="seg.span"
                  class="text-center grey--text text--lighten-1 pa-0"
                >
                  <span v-if="seg.span === 1">&mdash;</span>
                </td>
                <td
                  v-else
                  :key="'s-' + li + '-' + si"
                  :colspan="seg.span"
                  class="pa-1"
                >
                  <div class="restriction-bar rounded px-2 caption white--text d-flex align-center" :style="{ background: barColor(seg.restriction), whiteSpace: 'nowrap', overflow: 'hidden', lineHeight: '24px' }">
                    <v-tooltip v-if="seg.restriction.timespan === 'full_period'" bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <span class="fp-tag" v-bind="attrs" v-on="on">fp</span>
                      </template>
                      <span>{{ $t('components.TimeRestrictionsTable.fullPeriod') }}</span>
                    </v-tooltip>
                    <v-tooltip v-if="isMultiChannel(seg.restriction)" bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <span class="fp-tag group-tag" v-bind="attrs" v-on="on">{{ restrictionChannelsLabel(seg.restriction) }}</span>
                      </template>
                      <span>{{ $t('components.TimeRestrictionsTable.combinedRestriction', { channels: restrictionChannelsLabel(seg.restriction) }) }}</span>
                    </v-tooltip>
                    <span class="text-truncate flex-grow-1 text-center">{{ formatRestriction(seg.restriction) }}</span>
                    <v-menu offset-y left>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon x-small color="white" class="ml-1" v-bind="attrs" v-on="on" style="flex-shrink: 0;">mdi-chevron-down</v-icon>
                      </template>
                      <v-list dense>
                        <v-list-item @click="editRestriction(seg.restriction)">
                          <v-list-item-icon class="mr-2"><v-icon small>mdi-pencil</v-icon></v-list-item-icon>
                          <v-list-item-title>{{ $t('components.TimeRestrictionsTable.edit') }}</v-list-item-title>
                        </v-list-item>
                        <v-list-item @click="deleteRestriction(seg.restriction)">
                          <v-list-item-icon class="mr-2"><v-icon small color="red">mdi-delete</v-icon></v-list-item-icon>
                          <v-list-item-title class="red--text">{{ $t('components.TimeRestrictionsTable.delete') }}</v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </div>
                </td>
              </template>
            </tr>
          </template>
        </tbody>
      </template>
    </v-simple-table>

    <v-dialog v-model="addRestrictionDialog" max-width="500px" persistent>
      <v-card v-if="addRestrictionDialog">
        <v-card-title>
          {{ editingIndex === -1 ? $t('components.TimeRestrictionsTable.newRestrictionTitle') : $t('components.TimeRestrictionsTable.editRestrictionTitle') }}
        </v-card-title>
        <v-card-text>
          <v-select
            v-model="newRestriction.channelId"
            :items="channelItems"
            :label="$t('components.TimeRestrictionsTable.mediaChannel')"
            multiple
            chips
            small-chips
            deletable-chips
            :error="channelMissing"
            :error-messages="channelMissing ? [$t('components.TimeRestrictionsTable.mediaChannelRequired')] : []"
            dense
            outlined
            class="mb-2"
          ></v-select>

          <v-select
            v-model="newRestriction.type"
            :items="restrictionTypeItems"
            :label="$t('components.TimeRestrictionsTable.restrictionType')"
            dense
            outlined
            class="mb-2"
          ></v-select>

          <v-row v-if="newRestriction.type !== 'between'">
            <v-col cols="12">
              <v-text-field
                v-model.number="newRestriction.firstValue"
                :label="$t('components.TimeRestrictionsTable.value')"
                type="number"
                min="0"
                :rules="nonNegativeRules"
                dense
                outlined
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row v-if="newRestriction.type === 'between'">
            <v-col cols="6">
              <v-text-field
                v-model.number="newRestriction.firstValue"
                :label="$t('components.TimeRestrictionsTable.minValue')"
                type="number"
                min="0"
                :rules="nonNegativeRules"
                dense
                outlined
              ></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model.number="newRestriction.secondValue"
                :label="$t('components.TimeRestrictionsTable.maxValue')"
                type="number"
                min="0"
                :rules="maxValueRules"
                dense
                outlined
              ></v-text-field>
            </v-col>
          </v-row>

          <v-select
            v-model="newRestriction.timespan"
            :items="timespanItems"
            :label="$t('components.TimeRestrictionsTable.restrictionTimespan')"
            dense
            outlined
            class="mb-2"
          ></v-select>

          <v-row v-if="newRestriction.timespan === 'custom_range'">
            <v-col cols="6">
              <v-select
                v-model="newRestriction.fromDate"
                :items="periodDateItems"
                :label="$t('components.TimeRestrictionsTable.fromDate')"
                dense
                outlined
              ></v-select>
            </v-col>
            <v-col cols="6">
              <v-select
                v-model="newRestriction.toDate"
                :items="periodDateItems"
                :label="$t('components.TimeRestrictionsTable.toDate')"
                dense
                outlined
              ></v-select>
            </v-col>
          </v-row>
          <v-alert v-if="isDuplicate" type="error" dense class="mt-2 mb-0">
            {{ $t('components.TimeRestrictionsTable.duplicateError') }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="cancelDialog">
            {{ $t('components.TimeRestrictionsTable.cancel') }}
          </v-btn>
          <v-tooltip top :disabled="!disabledReason">
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">
                <v-btn color="primary" text :disabled="isDuplicate || !isValid" @click="saveRestriction">
                  {{ editingIndex === -1 ? $t('components.TimeRestrictionsTable.addRestriction') : $t('components.TimeRestrictionsTable.saveRestriction') }}
                </v-btn>
              </span>
            </template>
            <span>{{ disabledReason }}</span>
          </v-tooltip>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from 'moment'

import { timeRestrictionChannelIds } from '@/utils/timeRestrictions.js'

var ALL_CHANNELS_ID = '__all__'

// Distinct bar colors for multi-channel restrictions, so the bars of one
// restriction are recognizable as a group across the rows of its member
// channels. Picked away from the single-channel greys/blue (#90A4AE/#1976D2)
// and the legacy All-Channels purples (#B39DDB/#7E57C2); all keep white text
// readable.
var GROUP_COLORS = ['#7B1FA2', '#00838F', '#EF6C00', '#C2185B', '#558B2F', '#5D4037', '#303F9F', '#9E9D24']

export default {
  name: 'TimeRestrictionsTable',
  props: {
    channels: {
      type: Array,
      required: true
    },
    prettyNames: {
      type: Object,
      default: function () {
        return {}
      }
    },
    dates: {
      type: Array,
      required: true
    },
    start: {
      type: Number,
      required: true
    },
    length: {
      type: Number,
      required: true
    },
    value: {
      type: Array,
      default: function () {
        return []
      }
    },
    modelFrequency: {
      type: String,
      required: false,
      default: 'month'
    }
  },
  data: function () {
    return {
      ALL_CHANNELS_ID: ALL_CHANNELS_ID,
      addRestrictionDialog: false,
      editingIndex: -1,
      newRestriction: {
        channelId: [],
        type: 'exactly',
        firstValue: 0,
        secondValue: 0,
        timespan: 'full_period',
        fromDate: '',
        toDate: ''
      }
    }
  },
  computed: {
    periodDates: function () {
      return this.dates.slice(this.start, this.start + this.length)
    },
    formattedPeriodHeaders: function () {
      var self = this
      return this.periodDates.map(function (date) {
        return self.formatPeriodLabel(date)
      })
    },
    channelItems: function () {
      // The __all__ option was intentionally removed: new restrictions can
      // only target specific channels. Legacy plans whose persisted shape
      // still carries '__all__' restrictions continue to render through the
      // hasAllChannelRestrictions rows below and remain editable via those
      // rows' menus, so existing data isn't stranded.
      var self = this
      return this.channels.map(function (channel) {
        return { text: self.channelName(channel[0]), value: channel[0] }
      })
    },
    hasAllChannelRestrictions: function () {
      return this.value.some(function (r) {
        return timeRestrictionChannelIds(r).indexOf(ALL_CHANNELS_ID) !== -1
      })
    },
    // Lanes per channel id (plus the legacy __all__ row), computed once because
    // the template reads it both for the lane v-for and the name cell rowspan.
    channelLaneMap: function () {
      var self = this
      var map = {}
      this.channels.forEach(function (channel) {
        map[channel[0]] = self.getChannelLanes(channel[0])
      })
      map[ALL_CHANNELS_ID] = this.getChannelLanes(ALL_CHANNELS_ID)
      return map
    },
    restrictionTypeItems: function () {
      return [
        { text: this.$t('components.TimeRestrictionsTable.exactly'), value: 'exactly' },
        { text: this.$t('components.TimeRestrictionsTable.lessThan'), value: 'less_than' },
        { text: this.$t('components.TimeRestrictionsTable.moreThan'), value: 'more_than' },
        { text: this.$t('components.TimeRestrictionsTable.between'), value: 'between' }
      ]
    },
    timespanItems: function () {
      return [
        { text: this.$t('components.TimeRestrictionsTable.fullPeriod'), value: 'full_period' },
        { text: this.$t('components.TimeRestrictionsTable.customRange'), value: 'custom_range' }
      ]
    },
    periodDateItems: function () {
      var self = this
      return this.periodDates.map(function (date) {
        return { text: self.formatPeriodLabel(date), value: date }
      })
    },
    // Color per multi-channel restriction, keyed by its index in `value`.
    // Assignment follows value order, so colors are stable across re-renders
    // and only shift when an earlier multi-channel restriction is removed.
    multiChannelColors: function () {
      var map = {}
      var next = 0
      for (var i = 0; i < this.value.length; i++) {
        if (timeRestrictionChannelIds(this.value[i]).length > 1) {
          map[i] = GROUP_COLORS[next % GROUP_COLORS.length]
          next++
        }
      }
      return map
    },
    channelMissing: function () {
      return !this.newRestriction.channelId || this.newRestriction.channelId.length === 0
    },
    disabledReason: function () {
      // Mirrors the early-return order of isValid + isDuplicate so the
      // tooltip surfaces the same blocker the user would resolve first.
      // Purely cosmetic — :disabled on the button keeps reading from
      // isDuplicate || !isValid, so the two cannot diverge.
      var nr = this.newRestriction
      if (!nr.channelId || nr.channelId.length === 0) return this.$t('components.TimeRestrictionsTable.mediaChannelRequired')
      var first = Number(nr.firstValue)
      if (isNaN(first) || first < 0) return this.$t('components.TimeRestrictionsTable.valueMustBeNonNegative')
      if (nr.type === 'between') {
        var second = Number(nr.secondValue)
        if (isNaN(second) || second < 0) return this.$t('components.TimeRestrictionsTable.valueMustBeNonNegative')
        if (second <= first) return this.$t('components.TimeRestrictionsTable.maxMustBeGreaterThanMin')
      }
      if (nr.timespan === 'custom_range') {
        if (!nr.fromDate || !nr.toDate) return this.$t('components.TimeRestrictionsTable.selectDateRange')
        if (this.periodDates.indexOf(nr.fromDate) > this.periodDates.indexOf(nr.toDate)) {
          return this.$t('components.TimeRestrictionsTable.fromDateMustBeBeforeToDate')
        }
      }
      if (this.isDuplicate) return this.$t('components.TimeRestrictionsTable.duplicateError')
      return ''
    },
    isValid: function () {
      var nr = this.newRestriction
      if (!nr.channelId || nr.channelId.length === 0) return false
      // Reject negative values regardless of restriction type. Number('') is 0,
      // so an empty firstValue is treated as 0 (still non-negative).
      var first = Number(nr.firstValue)
      if (isNaN(first) || first < 0) return false
      if (nr.type === 'between') {
        var second = Number(nr.secondValue)
        if (isNaN(second) || second < 0) return false
        // Strict: a between [min, max] interval requires max > min
        if (second <= first) return false
      }
      if (nr.timespan === 'custom_range') {
        if (!nr.fromDate || !nr.toDate) return false
        if (this.periodDates.indexOf(nr.fromDate) > this.periodDates.indexOf(nr.toDate)) return false
      }
      return true
    },
    nonNegativeRules: function () {
      var self = this
      return [
        function (v) {
          // Allow blank while user is typing; isValid still gates the Save button.
          if (v === '' || v === null || v === undefined) return true
          return Number(v) >= 0 || self.$t('components.TimeRestrictionsTable.valueMustBeNonNegative')
        }
      ]
    },
    maxValueRules: function () {
      var self = this
      return [
        function (v) {
          if (v === '' || v === null || v === undefined) return true
          return Number(v) >= 0 || self.$t('components.TimeRestrictionsTable.valueMustBeNonNegative')
        },
        function (v) {
          if (v === '' || v === null || v === undefined) return true
          var min = Number(self.newRestriction.firstValue)
          if (isNaN(min)) return true
          return Number(v) > min || self.$t('components.TimeRestrictionsTable.maxMustBeGreaterThanMin')
        }
      ]
    },
    // Overlapping restrictions are allowed (that's the point of lanes), but an
    // EXACT duplicate — same channel set over the same effective window — is
    // either redundant or expressible as one 'between', so it stays blocked.
    isDuplicate: function () {
      var nr = this.newRestriction
      if (!nr.channelId || nr.channelId.length === 0) return false
      var newRange = this.resolveRange(nr)
      if (!newRange) return false
      var newIds = nr.channelId.slice().sort().join('\u0000')
      for (var i = 0; i < this.value.length; i++) {
        if (i === this.editingIndex) continue
        var ids = timeRestrictionChannelIds(this.value[i]).slice().sort().join('\u0000')
        if (ids !== newIds) continue
        var range = this.resolveRange(this.value[i])
        if (range && range[0] === newRange[0] && range[1] === newRange[1]) return true
      }
      return false
    }
  },
  methods: {
    formatPeriodLabel: function (date) {
      // Weekly periods are the sunday that closes the week (YYYY-MM-DD); show
      // it as "MMM D" so consecutive Sundays are distinguishable.
      // Monthly periods stay as "MMM 'YY".
      if (this.modelFrequency === 'week') {
        return moment(date).format('MMM D')
      }
      return moment(date).format("MMM 'YY")
    },
    channelName: function (channelId) {
      if (channelId === ALL_CHANNELS_ID) {
        return this.$t('components.TimeRestrictionsTable.allChannels')
      }
      return this.prettyNames && this.prettyNames[channelId] ? this.prettyNames[channelId] : channelId
    },
    restrictionChannelsLabel: function (restriction) {
      var self = this
      return timeRestrictionChannelIds(restriction).map(function (id) {
        return self.channelName(id)
      }).join(' + ')
    },
    isMultiChannel: function (restriction) {
      return timeRestrictionChannelIds(restriction).length > 1
    },
    barColor: function (restriction) {
      var index = this.value.indexOf(restriction)
      if (this.multiChannelColors[index] !== undefined) {
        return this.multiChannelColors[index]
      }
      return restriction.timespan === 'full_period' ? '#90A4AE' : '#1976D2'
    },
    getChannelRestrictions: function (channelId) {
      return this.value.filter(function (r) {
        return timeRestrictionChannelIds(r).indexOf(channelId) !== -1
      })
    },
    // Effective [startIdx, endIdx] of a restriction on the periodDates axis;
    // a full_period restriction equals a custom range covering everything.
    resolveRange: function (r) {
      var total = this.periodDates.length
      if (total === 0) return null
      if (r.timespan === 'full_period') return [0, total - 1]
      var rStart = this.periodDates.indexOf(r.fromDate)
      var rEnd = this.periodDates.indexOf(r.toDate)
      if (rStart === -1) rStart = 0
      if (rEnd === -1) rEnd = total - 1
      return [rStart, rEnd]
    },
    // Lanes for one channel: every restriction involving the channel, packed
    // into as few rows as possible (greedy interval partitioning over the
    // start-sorted items). Each lane has the segment shape the template renders
    // ([{type: 'empty'|'bar', span, restriction}]).
    getChannelLanes: function (channelId) {
      var restrictions = this.getChannelRestrictions(channelId)
      var total = this.periodDates.length
      if (restrictions.length === 0) {
        return [[{ type: 'empty', span: total }]]
      }
      var self = this
      var allValues = this.value
      var items = restrictions.map(function (r) {
        var range = self.resolveRange(r)
        return { restriction: r, start: range[0], end: range[1] }
      })
      items.sort(function (a, b) {
        if (a.start !== b.start) return a.start - b.start
        if (a.end !== b.end) return a.end - b.end
        return allValues.indexOf(a.restriction) - allValues.indexOf(b.restriction)
      })
      var laneItems = []
      var laneEnds = []
      items.forEach(function (item) {
        var placed = false
        for (var k = 0; k < laneEnds.length && !placed; k++) {
          if (item.start > laneEnds[k]) {
            laneItems[k].push(item)
            laneEnds[k] = item.end
            placed = true
          }
        }
        if (!placed) {
          laneItems.push([item])
          laneEnds.push(item.end)
        }
      })
      return laneItems.map(function (lane) {
        return self.buildLaneSegments(lane, total)
      })
    },
    buildLaneSegments: function (laneItems, total) {
      // Items within a lane never overlap by construction, so painting cannot
      // overwrite. Segments must carry the restriction object REFERENCE from
      // `value`: editRestriction/deleteRestriction resolve it via
      // value.indexOf(restriction), no matter which channel row it came from.
      var slots = new Array(total)
      var i, j
      for (i = 0; i < total; i++) {
        slots[i] = null
      }
      for (j = 0; j < laneItems.length; j++) {
        for (i = laneItems[j].start; i <= laneItems[j].end; i++) {
          slots[i] = laneItems[j].restriction
        }
      }
      // Collapse consecutive slots into segments
      var segments = []
      var current = slots[0]
      var span = 1
      for (i = 1; i < total; i++) {
        if (slots[i] === current) {
          span++
        } else {
          if (current === null) {
            segments.push({ type: 'empty', span: span })
          } else {
            segments.push({ type: 'bar', span: span, restriction: current })
          }
          current = slots[i]
          span = 1
        }
      }
      if (current === null) {
        segments.push({ type: 'empty', span: span })
      } else {
        segments.push({ type: 'bar', span: span, restriction: current })
      }
      return segments
    },
    sortChannelIds: function (channelIds) {
      // Stable group labels ("TV + Radio") regardless of selection click order.
      var order = this.channels.map(function (channel) { return channel[0] })
      return channelIds.slice().sort(function (a, b) {
        return order.indexOf(a) - order.indexOf(b)
      })
    },
    openAddDialog: function () {
      this.editingIndex = -1
      this.newRestriction = {
        channelId: [],
        type: 'exactly',
        firstValue: 0,
        secondValue: 0,
        timespan: 'full_period',
        fromDate: '',
        toDate: ''
      }
      this.addRestrictionDialog = true
    },
    editRestriction: function (restriction) {
      this.editingIndex = this.value.indexOf(restriction)
      this.newRestriction = {
        channelId: timeRestrictionChannelIds(restriction).slice(),
        type: restriction.type,
        firstValue: restriction.firstValue,
        secondValue: restriction.secondValue,
        timespan: restriction.timespan,
        fromDate: restriction.fromDate || '',
        toDate: restriction.toDate || ''
      }
      this.addRestrictionDialog = true
    },
    deleteRestriction: function (restriction) {
      // A multi-channel restriction renders one bar per member channel, all
      // pointing at the same object: deleting from any row removes the single
      // underlying restriction (and therefore every bar of it).
      var index = this.value.indexOf(restriction)
      if (index === -1) return
      var updated = this.value.slice()
      updated.splice(index, 1)
      this.$emit('input', updated)
    },
    saveRestriction: function () {
      var restriction = {
        channelId: this.sortChannelIds(this.newRestriction.channelId),
        type: this.newRestriction.type,
        firstValue: this.newRestriction.firstValue,
        secondValue: this.newRestriction.type === 'between' ? this.newRestriction.secondValue : 0,
        timespan: this.newRestriction.timespan,
        fromDate: this.newRestriction.timespan === 'custom_range' ? this.newRestriction.fromDate : null,
        toDate: this.newRestriction.timespan === 'custom_range' ? this.newRestriction.toDate : null
      }
      var updated = this.value.slice()
      if (this.editingIndex >= 0) {
        updated.splice(this.editingIndex, 1, restriction)
      } else {
        updated.push(restriction)
      }
      this.$emit('input', updated)
      this.addRestrictionDialog = false
    },
    cancelDialog: function () {
      this.editingIndex = -1
      this.addRestrictionDialog = false
    },
    formatNumber: function (value) {
      // Match the budget input's formatting (comma thousands, no decimals)
      // but without the spend-unit prefix.
      var num = Number(value)
      if (isNaN(num)) return value
      return Math.round(num).toLocaleString('en-US')
    },
    formatRestriction: function (r) {
      if (r.type === 'exactly') return '= ' + this.formatNumber(r.firstValue)
      if (r.type === 'less_than') return '≤ ' + this.formatNumber(r.firstValue)
      if (r.type === 'more_than') return '≥ ' + this.formatNumber(r.firstValue)
      if (r.type === 'between') return this.formatNumber(r.firstValue) + ' – ' + this.formatNumber(r.secondValue)
      return ''
    }
  }
}
</script>

<style scoped>
.fp-tag {
  flex-shrink: 0;
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  background: rgba(255, 255, 255, 0.25);
  border-radius: 3px;
  padding: 1px 4px;
  margin-right: 4px;
  letter-spacing: 0.5px;
  line-height: 14px;
}
.group-tag {
  max-width: 90px;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* Lanes of the same channel form one visual row: only the channel's last lane
   keeps Vuetify's bottom border, so stacked lanes don't read as extra channels.
   The rowspanned name cell is excluded: it lives in the FIRST lane's <tr> but
   spans down to the block's bottom, where its own border draws the left part
   of the divider. */
::v-deep tbody tr:not(.lane-last) td:not(.channel-name-cell) {
  border-bottom: none !important;
}
</style>
